import React, { useMemo, useState } from 'react';
import { cn } from '../../design-system/cn';
import { typography, radii, spacing, shadows } from '../../design-system/tokens';
import { Button, EmptyState, Card, SectionHeading } from '../ui';
import OpenArenaLockCard from './OpenArenaLockCard';
import SentChallengeTicketCard from '../ticket/SentChallengeTicketCard';
import { ArenaSegmentedTabs } from './ArenaLifecycleTabs';
import {
  OPEN_ARENA_EMPTY_COPY,
  OPEN_ARENA_SECTION_TITLE,
  START_A_LOCK,
} from '../../constants/productCopy';
import { isOpenTicket } from '../../utils/ticketStatus';

const TABS = [
  { id: 'available', label: 'Available' },
  { id: 'mine', label: 'My Open Locks' },
  { id: 'matched', label: 'Recently Matched' },
];

function MatchedRow({ challenge, onRunItBack, hideStake }) {
  const acceptor = challenge.acceptedByName || challenge.acceptorUsername || 'Opponent';
  return (
    <div className="rounded-2xl border border-white/10 bg-[#141923] p-3 flex items-center justify-between gap-2">
      <div className="min-w-0">
        <p className="text-xs font-bold text-[#f7f9fc] truncate">
          {challenge.creatorUsername || 'Player'}
          <span className="text-[#687386] mx-1.5">↔</span>
          {acceptor}
        </p>
        <p className="text-[10px] text-[#687386] font-medium truncate mt-0.5">
          {challenge.wager || 'Open lock'}
          {!hideStake && challenge.stake ? ` · $${challenge.stake} entry` : ''}
        </p>
      </div>
      <div className="flex flex-col items-end gap-1 shrink-0">
        <span className="text-[9px] font-black uppercase tracking-widest text-[#8cff4a]">
          Matched
        </span>
        {onRunItBack && (
          <button
            type="button"
            onClick={() => onRunItBack(challenge)}
            className="text-[10px] font-black uppercase tracking-widest text-[#33d6ff] hover:text-[#8cff4a]"
          >
            Run it back
          </button>
        )}
      </div>
    </div>
  );
}

export default function LiveArenaLobby({
  id,
  className,
  openChallenges = [],
  myOpenLocks = [],
  mySentTickets = [],
  recentlyMatched = [],
  currentUserId,
  creatorName,
  gameById = {},
  canUseCustomWagers = false,
  isGuest = false,
  loadError,
  sentTicketsError,
  acceptBusyId,
  cancelBusyId,
  ticketCancelBusyCode,
  onJoin,
  onCancel,
  onCancelTicket,
  onViewTicketChallenge,
  onCreateLock,
  onCreateChallenge,
  onRunItBack,
  hideMatchedStake = false,
  embedded = false,
  activeTab = 'available',
  onTabChange,
}) {
  const [tab, setTab] = useState(activeTab);

  React.useEffect(() => {
    setTab(activeTab);
  }, [activeTab]);

  const selectTab = (next) => {
    setTab(next);
    onTabChange?.(next);
  };

  const available = useMemo(
    () => openChallenges.filter((c) => c.creatorId !== currentUserId),
    [openChallenges, currentUserId]
  );

  const myOpenSentTickets = useMemo(
    () => mySentTickets.filter((t) => isOpenTicket(t)),
    [mySentTickets],
  );
  const myInactiveSentTickets = useMemo(
    () => mySentTickets.filter((t) => !isOpenTicket(t)),
    [mySentTickets],
  );

  const tabCounts = {
    available: available.length,
    mine: myOpenLocks.length + myOpenSentTickets.length,
    matched: recentlyMatched.length,
  };

  const Wrapper = embedded ? 'div' : 'section';

  return (
    <Wrapper
      id={embedded ? undefined : id}
      className={cn(
        !embedded && 'border border-[#8cff4a]/20 bg-[#141923]',
        !embedded && cn(radii.cardResponsive, spacing.card.sm, shadows.brandCard, 'scroll-mt-24'),
        embedded && 'min-w-0',
        className
      )}
      aria-label={embedded ? undefined : 'Live Arena lobby'}
    >
      {!embedded && (
        <SectionHeading
          eyebrow={OPEN_ARENA_SECTION_TITLE}
          icon="🔥"
          tone="brand"
          description="Browse open locks, manage yours, or see recent matches."
          className="mb-3"
        />
      )}

      {loadError && (
        <p className="text-xs font-bold text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-xl px-3 py-2 mb-3">
          {loadError}
        </p>
      )}

      {isGuest && (
        <p className="text-xs text-gray-400 font-medium mb-3">
          Sign in to join open locks or post to the arena.
        </p>
      )}

      <ArenaSegmentedTabs
        tabs={TABS.map((t) => ({
          id: t.id,
          label: t.label,
          count: tabCounts[t.id] ?? 0,
          panelId: `open-arena-panel-${t.id}`,
        }))}
        activeId={tab}
        onChange={selectTab}
        ariaLabel="Open arena views"
        data-testid="open-arena-subtabs"
        className="mb-3 md:mb-4"
      />

      {tab === 'available' && (
        <div className="space-y-2">
          {available.length === 0 ? (
            <EmptyState
              icon="🏟️"
              title="No open locks right now"
              description={OPEN_ARENA_EMPTY_COPY}
            >
              {(onCreateChallenge || onCreateLock) && (
                <Button
                  type="button"
                  onClick={onCreateChallenge || onCreateLock}
                  className="mt-4 bg-[#8cff4a] hover:brightness-110 text-[#07090d] text-[10px] uppercase tracking-widest"
                >
                  {START_A_LOCK}
                </Button>
              )}
            </EmptyState>
          ) : (
            available.map((c) => (
              <OpenArenaLockCard
                key={c.id}
                challenge={c}
                currentUserId={currentUserId}
                gameById={gameById}
                joinBusy={acceptBusyId === c.id}
                joinDisabled={isGuest}
                onJoin={onJoin}
              />
            ))
          )}
        </div>
      )}

      {tab === 'mine' && (
        <div className="space-y-3">
          {myOpenSentTickets.length > 0 && (
            <div className="space-y-2">
              <p className={`${typography.microWide} text-cyan-400/90`}>My Sent Tickets</p>
              {myOpenSentTickets.map((ticket) => (
                <SentChallengeTicketCard
                  key={ticket.ticketCode}
                  ticket={ticket}
                  creatorName={creatorName}
                  cancelBusy={ticketCancelBusyCode === ticket.ticketCode}
                  onCancel={onCancelTicket}
                  onViewChallenge={onViewTicketChallenge}
                />
              ))}
            </div>
          )}

          {myOpenLocks.length === 0 && myOpenSentTickets.length === 0 ? (
            <EmptyState
              icon="🔒"
              title="No open locks or tickets"
              description="Create a challenge or send a ticket link and wait for another player to join."
            >
              {(onCreateChallenge || (canUseCustomWagers && onCreateLock)) && (
                <Button
                  type="button"
                  onClick={onCreateChallenge || onCreateLock}
                  className="mt-4 bg-[#8cff4a] hover:brightness-110 text-[#07090d] text-[10px] uppercase tracking-widest"
                >
                  {onCreateChallenge ? START_A_LOCK : 'Post lock →'}
                </Button>
              )}
            </EmptyState>
          ) : (
            myOpenLocks.length > 0 && (
              <div className="space-y-2">
                {myOpenSentTickets.length > 0 && (
                  <p className={`${typography.microWide} text-emerald-400/90`}>My Open Locks</p>
                )}
                {myOpenLocks.map((c) => (
                  <OpenArenaLockCard
                    key={c.id}
                    challenge={c}
                    currentUserId={currentUserId}
                    gameById={gameById}
                    cancelBusy={cancelBusyId === c.id}
                    onCancel={onCancel}
                  />
                ))}
              </div>
            )
          )}

          {sentTicketsError && (
            <p className="text-xs font-bold text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-xl px-3 py-2">
              {sentTicketsError}
            </p>
          )}

          {myInactiveSentTickets.length > 0 && (
            <div className="space-y-2 pt-2 border-t border-gray-800/80">
              <p className={`${typography.microWide} text-gray-500`}>Past Tickets</p>
              {myInactiveSentTickets.map((ticket) => (
                <SentChallengeTicketCard
                  key={ticket.ticketCode}
                  ticket={ticket}
                  creatorName={creatorName}
                  onViewChallenge={onViewTicketChallenge}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {tab === 'matched' && (
        <div className="space-y-2">
          {recentlyMatched.length === 0 ? (
            <EmptyState
              icon="⚡"
              title="No recent matches yet"
              description="When players join open locks, matches show up here."
            />
          ) : (
            recentlyMatched.map((c) => (
              <MatchedRow
                key={c.id}
                challenge={c}
                onRunItBack={onRunItBack}
                hideStake={hideMatchedStake}
              />
            ))
          )}
        </div>
      )}
    </Wrapper>
  );
}
